import { difference, isPlainObject } from 'lodash';

interface Issue {
  path: (string | number)[];
  message: string;
}

type Issues = (value: unknown, path: (string | number)[]) => Issue[];

export interface Schema<T> {
  issues: Issues;
  output?: T;
  optional(): Optional<T>;
  nullable(): Schema<T | null>;
  refine(holds: (value: T) => boolean, message: string): Schema<T>;
}

export interface Optional<T> extends Schema<T | undefined> {
  mayBeLeftOut: true;
}

export type Infer<S> = S extends { output?: infer T } ? T : never;

type Fields = { [key: string]: Schema<unknown> };

type LeftOutKeys<Shape extends Fields> = {
  [K in keyof Shape]: Shape[K] extends { mayBeLeftOut: true } ? K : never;
}[keyof Shape];

type Flat<T> = { [K in keyof T]: T[K] };

type ObjectOf<Shape extends Fields> = Flat<
  { [K in Exclude<keyof Shape, LeftOutKeys<Shape>>]: Infer<Shape[K]> } & {
    [K in LeftOutKeys<Shape>]?: Infer<Shape[K]>;
  }
>;

const make = <T>(issues: Issues): Schema<T> => ({
  issues,
  optional: () => ({
    ...make<T | undefined>((value, path) => (value === undefined ? [] : issues(value, path))),
    mayBeLeftOut: true,
  }),
  nullable: () => make<T | null>((value, path) => (value === null ? [] : issues(value, path))),
  refine: (holds, message) =>
    make<T>((value, path) => {
      const found = issues(value, path);
      return found.length || holds(value as T) ? found : [{ path, message }];
    }),
});

const typed = <T>(fits: (value: unknown) => boolean, what: string): Schema<T> =>
  make<T>((value, path) => (fits(value) ? [] : [{ path, message: `should be ${what}` }]));

const string = () => typed<string>(value => typeof value === 'string', 'a text');
const number = () => typed<number>(value => typeof value === 'number' && Number.isFinite(value), 'a number');
const boolean = () => typed<boolean>(value => typeof value === 'boolean', 'true or false');
const unknown = () => make<unknown>(() => []);

const literal = <const V extends string | number | boolean>(expected: V) =>
  typed<V>(value => value === expected, `“${expected}”`);

const oneOf = <const V extends readonly string[]>(values: V) =>
  typed<V[number]>(value => values.includes(value as string), `one of ${values.map(v => `“${v}”`).join(', ')}`);

const array = <T>(item: Schema<T>) =>
  make<T[]>((value, path) =>
    Array.isArray(value)
      ? value.flatMap((each, index) => item.issues(each, [...path, index]))
      : [{ path, message: 'should be a list' }],
  );

const record = <T>(item: Schema<T>) =>
  make<Record<string, T>>((value, path) =>
    isPlainObject(value)
      ? Object.entries(value as Record<string, unknown>).flatMap(([key, each]) => item.issues(each, [...path, key]))
      : [{ path, message: 'should be an object' }],
  );

const strictObject = <const Shape extends Fields>(shape: Shape) =>
  make<ObjectOf<Shape>>((value, path) => {
    if (!isPlainObject(value)) return [{ path, message: 'should be an object' }];
    const object = value as Record<string, unknown>;
    const strangers = difference(Object.keys(object), Object.keys(shape));
    return [
      ...strangers.map(key => ({ path, message: `has a key it can't have, “${key}”` })),
      ...Object.entries(shape).flatMap(([key, schema]) =>
        Object.hasOwn(object, key) || 'mayBeLeftOut' in schema
          ? schema.issues(object[key], [...path, key])
          : [{ path, message: `has no “${key}”` }],
      ),
    ];
  });

const union = <const Members extends Schema<unknown>[]>(members: Members) =>
  make<Infer<Members[number]>>((value, path) =>
    members.some(member => member.issues(value, path).length === 0)
      ? []
      : [{ path, message: "isn't any of the values it can be" }],
  );

const byKey = <const Members extends Record<string, Schema<unknown>>>(key: string, members: Members) =>
  make<Infer<Members[keyof Members]>>((value, path) => {
    if (!isPlainObject(value)) return [{ path, message: 'should be an object' }];
    const object = value as Record<string, unknown>;
    if (!Object.hasOwn(object, key)) return [{ path, message: `has no "${key}"` }];
    const tag = object[key];
    const member = typeof tag === 'string' && Object.hasOwn(members, tag) ? members[tag] : undefined;
    if (member) return member.issues(value, path);
    return [{ path, message: `has unknown "${key}", ${String(tag)}` }];
  });

const lazy = <T>(get: () => Schema<T>) => make<T>((value, path) => get().issues(value, path));

export const s = {
  string,
  number,
  boolean,
  unknown,
  literal,
  enum: oneOf,
  array,
  record,
  strictObject,
  union,
  byKey,
  lazy,
};

export const check = <T>(
  schema: Schema<T>,
  value: unknown,
): { ok: true; value: T } | { ok: false; issues: Issue[] } => {
  const issues = schema.issues(value, []);
  return issues.length ? { ok: false, issues } : { ok: true, value: value as T };
};
